import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { FileMetadata, FileSearchFilters } from '../../services/fileService';
import { cn } from '../../lib/utils';

interface ChatFileFiltersProps {
  metadata: FileMetadata;
  filters: FileSearchFilters;
  hasActiveFilters: boolean;
  onToggle: (type: keyof FileSearchFilters, value: string) => void;
  onClear: () => void;
}

const MAX_TAGS = 8;

const FilterChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'h-6 shrink-0 rounded-full border px-2 text-[0.6875rem] transition-colors',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
    )}
  >
    {children}
  </button>
);

/** Category / type / tag facets above the file list. */
const ChatFileFilters: React.FC<ChatFileFiltersProps> = ({
  metadata,
  filters,
  hasActiveFilters,
  onToggle,
  onClear,
}) => {
  const { t } = useTranslation();

  const groups = [
    { key: 'categories' as const, values: metadata.categories, format: (v: string) => v },
    { key: 'fileTypes' as const, values: metadata.fileTypes, format: (v: string) => v.toUpperCase() },
    { key: 'tags' as const, values: metadata.tags.slice(0, MAX_TAGS), format: (v: string) => v },
  ];

  if (groups.every((group) => group.values.length === 0)) return null;

  return (
    <div className="flex max-h-20 flex-wrap gap-1 overflow-y-auto border-b px-2.5 py-2">
      {groups.flatMap((group) =>
        group.values.map((value) => (
          <FilterChip
            key={`${group.key}-${value}`}
            active={Boolean(filters[group.key]?.includes(value))}
            onClick={() => onToggle(group.key, value)}
          >
            {group.format(value)}
          </FilterChip>
        )),
      )}

      {hasActiveFilters && (
        <FilterChip active={false} onClick={onClear}>
          <span className="flex items-center gap-0.5">
            <X className="size-3" strokeWidth={2.4} />
            {t('common.clearAll')}
          </span>
        </FilterChip>
      )}
    </div>
  );
};

export default ChatFileFilters;
